import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  CircularProgress, Typography, Grid, FormControlLabel, Checkbox,
} from '@mui/material';
import dayjs from 'dayjs';
import { InvTextField } from '../InvField';
import { acceptVendorQuote } from '../../api/rfq';
import { createPoFromAcceptedQuote } from '../../api/procurement';
import { toAcceptQuoteBody, toPoFromQuoteBody } from '../../utils/rfqEntity';
import { generateTxnNumber } from '../../utils';
import { IPD_COLORS, cardSx, inputSx } from '../../theme';

export default function RfqAwardDialog({
  open,
  quote,
  onClose,
  onSuccess,
}) {
  const quoteId = quote?.vendor_quote_id || quote?.id;
  const [remarks, setRemarks] = useState('');
  const [rejectOthers, setRejectOthers] = useState(true);
  const [createPo, setCreatePo] = useState(true);
  const [poNumber, setPoNumber] = useState(() => generateTxnNumber('PO'));
  const [poDate, setPoDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [expectedDelivery, setExpectedDelivery] = useState(dayjs().add(30, 'day').format('YYYY-MM-DD'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  async function handleSubmit() {
    if (!quoteId) return;
    setLoading(true);
    setError(null);
    try {
      const accepted = await acceptVendorQuote(quoteId, toAcceptQuoteBody({ remarks, reject_others: rejectOthers }));
      const acceptedQuoteId = accepted?.id || accepted?.accepted_quote_id;

      let poId = null;
      if (createPo && acceptedQuoteId) {
        const po = await createPoFromAcceptedQuote(toPoFromQuoteBody(acceptedQuoteId, {
          po_number: poNumber,
          po_date: poDate,
          expected_delivery_date: expectedDelivery,
          remarks,
        }));
        poId = po?.id || po?.purchase_order_id;
      }

      onSuccess?.({ acceptedQuoteId, poId });
      onClose?.();
    } catch (e) {
      setError(e.message || 'Failed to accept quote');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { ...cardSx, p: 0.5 } }}
    >
      <DialogTitle sx={{ color: IPD_COLORS.textPrimary }}>
        Accept Quote &amp; Award RFQ
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary, mb: 2 }}>
          Accept quote <strong>{quote?.quote_number || quoteId}</strong> from{' '}
          <strong>{quote?.vendor_name || quote?.vendor_id}</strong>.
          This will mark the RFQ as <strong>AWARDED</strong>.
        </Typography>

        {error && (
          <Typography sx={{ color: IPD_COLORS.error.text, fontSize: 13, mb: 2 }}>{error}</Typography>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <InvTextField
              label="Acceptance Remarks"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={rejectOthers}
                  onChange={(e) => setRejectOthers(e.target.checked)}
                  sx={{ color: IPD_COLORS.primary }}
                />
              )}
              label="Reject all other quotes"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={createPo}
                  onChange={(e) => setCreatePo(e.target.checked)}
                  sx={{ color: IPD_COLORS.primary }}
                />
              )}
              label="Create Purchase Order from accepted quote"
            />
          </Grid>
          {createPo && (
            <>
              <Grid item xs={12} sm={6}>
                <InvTextField label="PO Number" value={poNumber} onChange={(e) => setPoNumber(e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InvTextField label="PO Date" type="date" value={poDate}
                  onChange={(e) => setPoDate(e.target.value)} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12}>
                <InvTextField label="Expected Delivery" type="date" value={expectedDelivery}
                  onChange={(e) => setExpectedDelivery(e.target.value)} InputLabelProps={{ shrink: true }} />
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onClose} disabled={loading} variant="outlined"
          sx={{ color: IPD_COLORS.textSecondary, borderColor: IPD_COLORS.border }}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={loading} variant="contained"
          sx={{ backgroundColor: IPD_COLORS.primary }}
          startIcon={loading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null}>
          Accept &amp; Award
        </Button>
      </DialogActions>
    </Dialog>
  );
}
